<template>
  <SubscribeAccordionItem
    class="subscribe__step third"
    :isOpen="isOpen"
    :disabled="disabled"
    disabled-tooltip-id="subscribe_step_3_disabled_tooltip"
    @toggle="$emit('toggle')"
  >
    <template #head>
      <div>
        <div class="subscribe__step-label">{{t('label')}}</div>
        <h3 class="subscribe__step-header h6">
          <LegalText :text="t('header')" />
        </h3>
        <div class="subscribe__step-subheader">{{t('subheader')}}</div>
      </div>
    </template>

    <template #toggler-content>
      <div
        v-if="disabled"
        id="subscribe_step_3_disabled_tooltip"
        class="subscribe__step-toggler--tooltip"
        role="tooltip"
        v-html="t('tooltip')"
      ></div>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="10" viewBox="0 0 20 10" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M19.6711 9.66434C20.1227 9.22954 20.1073 8.53882 19.6367 8.12159L10.8178 0.303836C10.3608 -0.101278 9.63923 -0.101278 9.18223 0.303836L0.363338 8.12159C-0.107325 8.53882 -0.122746 9.22954 0.328894 9.66434C0.780533 10.0991 1.52821 10.1134 1.99887 9.69616L10 2.60334L18.0011 9.69616C18.4718 10.1134 19.2195 10.0991 19.6711 9.66434Z" fill="#2A2C2C"/>
      </svg>
    </template>

    <div class="subscribe__step-delivery">
      <div class="subscribe__step-delivery-title">{{t('deliveryTitle')}}</div>

      <div class="input-group autocomplete">
        <label for="np_city">
          {{ t('cityLabel') }} <span class="input-group__required" aria-hidden="true">*</span>
        </label>
        <input
          ref="cityInputEl"
          id="np_city"
          role="combobox"
          type="text"
          required
          aria-required="true"
          :class="{ 'input--error': cityError }"
          :aria-invalid="cityError ? 'true' : undefined"
          :aria-errormessage="cityError ? 'np_city_error' : undefined"
          v-model="cityQuery"
          @input="onCityInput"
          @focus="onCityFocus"
          @blur="onCityBlur"
          @keydown="onCityKeydown"
          :placeholder="t('cityPlaceholder')"
          :aria-expanded="cityListVisible ? 'true' : 'false'"
          :aria-controls="cityListboxId"
          :aria-activedescendant="activeCityOptionId || undefined"
          aria-autocomplete="list"
          autocorrect="off"
          autocomplete="off"
        />
        <div v-if="cityError" id="np_city_error" class="error">{{ cityError }}</div>
        <div
          v-if="cityListVisible"
          class="sr-only"
          role="status"
          aria-live="polite"
        >
          {{ suggestionsStatus(citySuggestions.length) }}
        </div>
        <div
          v-if="cityListVisible"
          :id="cityListboxId"
          class="checkout-autocomplete"
          role="listbox"
        >
          <div
            v-for="(item, index) in citySuggestions"
            :id="getCityOptionId(index)"
            :key="item.city_id ?? item.value ?? item.label"
            class="ac-item"
            role="option"
            :aria-selected="index === cityActiveIndex ? 'true' : 'false'"
            @mousedown.prevent="selectCity(item)"
            @mouseenter="cityActiveIndex = index"
          >
            {{ item.label }}
          </div>
        </div>
      </div>

      <div class="subscribe__step-delivery-department" :class="{ active: !!deliveryCityId }">
        <div class="subscribe__step-delivery-department-radiokmark"></div>

        <div class="subscribe__step-delivery-department-main">
          <div class="subscribe__step-delivery-department-np">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clip-path="url(#clip0_4816_83816)">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M18.7571 7.42722C18.8248 7.40563 18.914 7.44882 19.0032 7.56756C19.0032 7.56756 19.0032 7.56756 23.0336 11.5474C23.269 11.7849 23.269 12.1448 23.0336 12.3211C23.0336 12.3211 23.0336 12.3211 19.0032 16.3622C18.914 16.4809 18.8248 16.5097 18.7571 16.4737C18.6893 16.4377 18.6465 16.3334 18.6465 16.1822V7.68631C18.6465 7.53878 18.6893 7.44881 18.7571 7.42722Z" fill="#ED1C24"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M11.8621 0.796875H12.1475L12.4221 0.912026C12.4221 0.912026 12.4221 0.912026 16.5703 5.07183C16.7486 5.30933 16.688 5.48925 16.3919 5.48925C16.3919 5.48925 16.3919 5.48925 14.6727 5.48925C14.3767 5.48925 14.1413 5.72675 14.1413 6.02542C14.1413 6.02542 14.1413 6.02542 14.1413 9.11289C14.1413 9.41156 13.9023 9.64906 13.5456 9.64906C13.5456 9.64906 13.5456 9.64906 10.5246 9.64906C10.2286 9.64906 9.98959 9.41156 9.98959 9.11289C9.98959 9.11289 9.98959 9.11289 9.98959 6.02542C9.98959 5.72675 9.75418 5.48925 9.45457 5.48925H7.6177C7.32165 5.48925 7.26102 5.30933 7.43936 5.07183C7.43936 5.07183 7.43936 5.07183 11.5911 0.912026L11.8621 0.796875Z" fill="#ED1C24"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M5.36371 7.34262C5.43861 7.3786 5.48498 7.48296 5.48498 7.63409V16.3064C5.48498 16.4575 5.43861 16.5475 5.36371 16.5762C5.29238 16.605 5.18894 16.5762 5.06767 16.4863C5.06767 16.4863 5.06767 16.4863 0.976603 12.3265C0.741197 12.1501 0.741197 11.7903 0.976603 11.5528C0.976603 11.5528 0.976604 11.5528 5.06767 7.45417C5.18894 7.33542 5.29238 7.30663 5.36371 7.34262Z" fill="#ED1C24"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5246 14.1641C10.5246 14.1641 10.5246 14.1641 13.5457 14.1641C13.9024 14.1641 14.1413 14.4016 14.1413 14.7002C14.1413 14.7002 14.1413 14.7002 14.1413 17.9676C14.1413 18.3239 14.3767 18.5614 14.6728 18.5614H16.2742C16.5703 18.5614 16.688 18.7377 16.4526 18.9176C16.4526 18.9176 16.4526 18.9176 12.4222 23.0162C12.3009 23.135 12.1546 23.1962 12.0048 23.1962C11.8586 23.1962 11.7088 23.135 11.5911 23.0162C11.5911 23.0162 11.5911 23.0162 7.56067 18.9176C7.3217 18.7377 7.4394 18.5614 7.73544 18.5614C7.73544 18.5614 7.73544 18.5614 9.45461 18.5614C9.75422 18.5614 9.98963 18.3239 9.98963 17.9676C9.98963 17.9676 9.98963 17.9676 9.98963 14.7002C9.98963 14.4016 10.2286 14.1641 10.5246 14.1641Z" fill="#ED1C24"/>
              </g>
              <defs>
                <clipPath id="clip0_4816_83816">
                  <rect width="24" height="24" fill="white"/>
                </clipPath>
              </defs>
            </svg>
            <span>{{t('npLabel')}}</span>
          </div>

          <div class="input-group autocomplete">
            <label for="np_department">
              {{ t('npNumber') }} <span class="input-group__required" aria-hidden="true">*</span>
            </label>
            <input
              ref="departmentInputEl"
              id="np_department"
              role="combobox"
              required
              aria-required="true"
              :class="{ 'input--error': departmentError }"
              :aria-invalid="departmentError ? 'true' : undefined"
              :aria-errormessage="departmentError ? 'np_department_error' : undefined"
              type="text"
              v-model="departmentQuery"
              :disabled="!deliveryCityId && !departmentQuery"
              @input="onDepartmentInput"
              @focus="onDepartmentFocus"
              @blur="onDepartmentBlur"
              @keydown="onDepartmentKeydown"
              autocomplete="off"
              :placeholder="t('npNumberPlaceholder')"
              :aria-expanded="departmentListVisible ? 'true' : 'false'"
              :aria-controls="departmentListboxId"
              :aria-activedescendant="activeDepartmentOptionId || undefined"
              aria-autocomplete="list"
            />
            <div v-if="departmentError" id="np_department_error" class="error">{{ departmentError }}</div>
            <div
              v-if="departmentListVisible"
              class="sr-only"
              role="status"
              aria-live="polite"
            >
              {{ suggestionsStatus(departmentSuggestions.length) }}
            </div>

            <div
              v-if="departmentListVisible"
              :id="departmentListboxId"
              class="checkout-autocomplete"
              role="listbox"
            >
              <div
                v-for="(item, index) in departmentSuggestions"
                :id="getDepartmentOptionId(index)"
                :key="item.meta?.number ?? item.value ?? item.label"
                class="ac-item"
                role="option"
                :aria-selected="index === departmentActiveIndex ? 'true' : 'false'"
                @mousedown.prevent="selectDepartment(item)"
                @mouseenter="departmentActiveIndex = index"
              >
                {{ item.label ?? item.value }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="subscribe__step-delivery-disclaimer">
        {{ t('deliveryDisclaimer') }}
      </div>
      <!-- PAYMENT -->
      <div class="subscribe__step-delivery-pay">
        <div class="group">
          <fieldset class="radio-group">
            <legend class="subscribe__step-delivery-title">{{t('payTitle')}}</legend>

            <div v-if="isLiqPayEnabled" class="radio-item" :class="{ 'radio-item--active': paymentType == '2' }">
              <input
                type="radio"
                class="radio-item__input"
                name="payment-type"
                id="payment-type-liqpay"
                value="2"
                v-model="paymentType"
              >
              <label class="radio-item__label" for="payment-type-liqpay">
                <span class="radio-item__box" aria-hidden="true"></span>
                <span>Оплата Liqpay</span>
              </label>
            </div>

            <div class="radio-item" :class="{ 'radio-item--active': paymentType == '1' }">
              <input
                type="radio"
                class="radio-item__input"
                name="payment-type"
                id="payment-type-gateway"
                value="1"
                v-model="paymentType"
              >
              <label class="radio-item__label" for="payment-type-gateway">
                <span class="radio-item__box" aria-hidden="true"></span>
                <span>{{t('paymentUponReceipt')}}</span>
              </label>
            </div>
          </fieldset>
        </div>
      </div>

      <!-- REMINDER -->
      <div class="subscribe__step-delivery-reminder">
        <div class="subscribe__step-delivery-title" v-html="t('remindTitle')"></div>

        <div class="subscribe__step-delivery-reminder-wrapper">
          <div class="input-group">
            <label for="remind-for" id="remind-for-ts-label">{{t('remindLabel')}}</label>

            <div class="select">
              <select
                id="remind-for"
                ref="remindSelectEl"
                name="Remind for"
                v-model="remindDays"
              >
                <option value="1">1 день</option>
                <option value="2">2 дня</option>
                <option value="3">3 дня</option>
              </select>

              <div class="select__icon select__icon--arrow" role="presentation">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd"
                    d="M26.3974 10.9363C26.7565 11.2954 26.7565 11.8776 26.3974 12.2367L17.5698 21.0643C17.3974 21.2368 17.1635 21.3337 16.9196 21.3337C16.6757 21.3337 16.4419 21.2368 16.2694 21.0643L7.44182 12.2367C7.08272 11.8776 7.08272 11.2954 7.44182 10.9363C7.80093 10.5772 8.38315 10.5772 8.74225 10.9363L16.9196 19.1137L25.097 10.9363C25.4561 10.5772 26.0383 10.5772 26.3974 10.9363Z"
                    fill="#2A2C2C"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div class="input-group">
            <label for="phone">
              Телефон <span class="input-group__required" aria-hidden="true">*</span>
            </label>
            <input
              ref="phoneInputEl"
              type="text"
              id="phone"
              required
              aria-required="true"
              v-model="phoneLocal"
              :class="{ 'input--error': phoneError }"
              :aria-invalid="phoneError ? 'true' : undefined"
              :aria-errormessage="phoneError ? 'phone-err' : undefined"
              @blur="validatePhone"
            />
            <div v-if="phoneError" id="phone-err" class="error">{{ phoneError }}</div>
          </div>
        </div>

        <div class="input-group">
          <label for="comment">{{ t('commentLabel') }}</label>
          <textarea
            name="comment"
            id="comment"
            v-model="commentLocal"
            :placeholder="t('commentPlaceholder')"
          ></textarea>
        </div>

        <div class="input-group">
          <label for="customer_agreement" class="input-group__wrapper--checkmark" :class="{ 'error': agreementError }">
            <input
              ref="agreementInputEl"
              type="checkbox"
              value="1"
              name="customer_agreement"
              id="customer_agreement"
              required
              aria-required="true"
              :aria-invalid="agreementError ? 'true' : undefined"
              :aria-errormessage="agreementError ? 'agreement-err' : undefined"
              v-model="agreementLocal"
            >
            <span class="checkmark">
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="9" viewBox="0 0 11 9" fill="none">
                <path d="M0.5 3.875L4 8L10.5 0.5" stroke="#2A2C2C" stroke-miterlimit="10" stroke-linecap="round"/>
              </svg>
            </span>

            <span class="input-group__agreement">
              <span v-html="t('checkmarkText')"></span>
              <span class="input-group__required" aria-hidden="true">*</span>
            </span>
          </label>

          <div v-if="agreementError" id="agreement-err" class="error">
            {{ agreementError }}
          </div>
        </div>
      </div>
    </div>
  </SubscribeAccordionItem>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import SubscribeAccordionItem from './SubscribeAccordionItem.vue'
import TomSelect from 'tom-select'

import { usePhoneMask } from './../composables/usePhoneMask'
import { PHONE_PLACEHOLDER, normalizePhoneDigits } from '@/js/autorization/phone.utils'
import LegalText from '../../js/components/LegalText.vue'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  customer: { type: Object, required: true },

  delivery: { type: Object, default: () => ({}) },
  payType: { type: [String, Number], default: '' },
  isLiqPayEnabled: { type: Boolean, default: false },
  phone: { type: String, default: '' },
  note: { type: String, default: '' },
  notifyDays: { type: [String, Number], default: '' },
  agreement: { type: Boolean, default: false },
})

const emit = defineEmits([
  'toggle',
  'update:delivery',
  'update:payType',
  'update:phone',
  'update:note',
  'update:notifyDays',
  'update:agreement',
])

const i18n = {
  uk: {
    label: '3 крок',
    header: 'Доставка@legal(delivery) та оплата',
    subheader: 'Зазнач відділення для доставки та спосіб оплати',
    tooltip: ' Буде доступний після <span class="block">заповнення <span class="orange">2 кроку</span></span>',
    deliveryTitle: 'Дані доставки',
    deliveryDisclaimer:
      "Доставка здійснюється по всій території України, за винятком територій, які у зв'язку з воєнною агресією рф, окуповані.",
    cityLabel: 'Населений пункт',
    cityPlaceholder: 'Введи населений пункт',
    cityErrorEmpty: 'Вкажіть населений пункт',
    cityErrorNoChoose: 'Оберіть населений пункт зі списку',
    cityErrorNoDelivery: 'Для цього населеного пункту немає доставки, оберіть інший',
    suggestionsOne: 'Доступна 1 підказка',
    suggestionsMany: 'Доступно підказок:',
    npLabel: 'До відділення Нової пошти',
    npNumber: 'Номер відділення',
    npNumberPlaceholder: 'Введи номер відділення (число)',
    npErrorEmpty: 'Вкажіть відділення',
    npErrorNoChoose: 'Оберіть відділення зі списку',
    payTitle: 'Спосіб оплати',
    paymentUponReceipt: 'Оплата при отриманні',
    remindTitle:
      '<span class="block">Нагадування про підтвердження</span>щомісячного замовлення (СМС на номер)',
    remindLabel: 'Нагадати',
    commentLabel: 'Коментар до замовлення',
    phoneRequired: 'Введіть номер телефону',
    phoneNotFull: 'Введіть номер повністю',
    phoneNotValid: 'Невірний формат номера',
    phoneOperatorNotValid: 'Невідомий код оператора',
    commentPlaceholder: "Залиш уточнюючий коментар (не обовʼязково)",
    checkmarkError: 'Потрібно погодитись з умовами',
    checkmarkText:
      'Я погоджуюсь з <a class="agreement_link" target="_blank" rel="nofollow noopener noreferrer" href="/account#club:rules">Умовами оформлення щомісячної підписки</a> та <a class="agreement_link" rel="nofollow noopener noreferrer" target="_blank" href="/pages/ugoda-z-korystuvachem">Угодою із користувачем</a>',
  },
  ru: {
    label: '3 шаг',
    header: 'Доставка@legal(delivery) и оплата',
    subheader: 'Укажи отделение для доставки и способ оплаты',
    buttonLabel: 'просмотреть все',
    tooltip: ' Будет доступен после <span class="block">заполнения <span class="orange">2 шага</span></span>',
    deliveryTitle: 'Данные доставки',
    deliveryDisclaimer:
      ' Доставка осуществляется по всей территории Украины, за исключением территорий, которые в связи с военной агрессией РФ оккупированы.',
    cityLabel: 'Населенный пункт',
    cityPlaceholder: 'Введи населенный пункт',
    cityErrorEmpty: 'Укажите населенный пункт',
    cityErrorNoChoose: 'Выберите населенный пункт из списка',
    cityErrorNoDelivery: 'Для этого населенного пункта нет доставки, выберите другой',
    suggestionsOne: 'Доступна 1 подсказка',
    suggestionsMany: 'Доступно подсказок:',
    npLabel: 'К отделению Новой почты',
    npNumber: 'Номер отделения',
    npNumberPlaceholder: 'Введи номер відділення (число)',
    npErrorEmpty: 'Укажите отделение',
    npErrorNoChoose: 'Выберите отделение из списка',
    payTitle: 'Способ оплаты',
    paymentUponReceipt: 'Оплата при получении',
    remindTitle:
      '<span class="block">Напоминание о подтверждении</span>ежемесячного заказа (СМС на номер)',
    remindLabel: 'Напомнить',
    commentLabel: 'Комментарий к заказу',
    phoneRequired: 'Введите номер телефона',
    phoneNotFull: 'Введите номер полностью',
    phoneNotValid: 'Неверный формат номера',
    phoneOperatorNotValid: 'Неизвестный код оператора',
    commentPlaceholder: 'Оставь уточняющий комментарий (не обязательно)',
    checkmarkError: 'Необходимо согласиться с условиями',
    checkmarkText:
      'Я согласен с <a class="agreement_link" target="_blank" href="/ru/account#club:rules">Условиями оформления ежемесячной подписки</a> и <a class="agreement_link" target="_blank" rel="nofollow noopener noreferrer" href="/ru/pages/ugoda-z-korystuvachem">Соглашением с пользователем</a>',
  },
}

const locale = computed(() => window.Shopify?.locale || 'uk')
const t = (key) => i18n[locale.value]?.[key] || key
const suggestionsStatus = (count) => (
  count === 1 ? t('suggestionsOne') : `${t('suggestionsMany')} ${count}`
)

const paymentType = ref('')
const remindDays = ref('')
const commentLocal = ref('')
const agreementLocal = ref(false)
const agreementError = ref('')
const phoneLocal = ref('')
const phoneError = ref('')

const cityInputEl = ref(null)
const departmentInputEl = ref(null)
const agreementInputEl = ref(null)

const {
  inputEl: phoneInputEl,
  state: phoneState,
  mask,
  setMaskedValue,
} = usePhoneMask(phoneLocal, {
  placeholder: PHONE_PLACEHOLDER,
})

watch(
  () => props.payType,
  (v) => {
    paymentType.value = v == null ? '' : String(v)
  },
  { immediate: true }
)

watch(
  () => props.notifyDays,
  (v) => {
    remindDays.value = v == null ? '' : String(v)
  },
  { immediate: true }
)

watch(
  () => props.note,
  (v) => {
    commentLocal.value = String(v ?? '')
  },
  { immediate: true }
)

watch(
  () => props.agreement,
  (v) => {
    agreementLocal.value = !!v
  },
  { immediate: true }
)

watch(
  () => props.phone,
  (v) => {
    if (!phoneState.value?.hasAnyInput) {
      phoneLocal.value = String(v ?? '')
    }
  },
  { immediate: true }
)

watch(paymentType, (v) => emit('update:payType', String(v ?? '')))
watch(remindDays, (v) => emit('update:notifyDays', String(v ?? '')))
watch(commentLocal, (v) => emit('update:note', String(v ?? '')))
watch(agreementLocal, (v) => emit('update:agreement', !!v))
watch(phoneLocal, (v) => emit('update:phone', String(v ?? '')))

const remindSelectEl = ref(null)
let remindSelect = null

function destroyRemindSelect() {
  if (!remindSelect) return
  remindSelect.destroy()
  remindSelect = null
}

function initRemindSelect() {
  if (remindSelect || !remindSelectEl.value) return
  if (props.isLoading) return

  const selectEl = remindSelectEl.value
  const labelId = 'remind-for-ts-label'
  const labelText = t('remindLabel')

  remindSelect = new TomSelect(selectEl, {
    controlInput: null,
    searchField: [],
    create: false,
    onChange: (val) => {
      remindDays.value = String(val || '')
    },
    onInitialize() {
      const ts = this
      const control = ts.control
      const dropdown = ts.dropdown_content || ts.dropdown

      selectEl.setAttribute('aria-label', labelText)
      selectEl.setAttribute('aria-labelledby', labelId)

      const labelEl = document.getElementById(labelId)
      if (labelEl) {
        labelEl.setAttribute('for', 'remind-for')
      }

      if (control) {
        control.setAttribute('aria-label', labelText)
        control.setAttribute('aria-labelledby', labelId)
      }

      if (dropdown) {
        dropdown.setAttribute('aria-labelledby', labelId)
      }
    },
  })

  if (remindDays.value) {
    remindSelect.setValue(String(remindDays.value), true)
  }
}

watch(remindDays, (v) => {
  if (!remindSelect) return
  const value = String(v || '')
  if (!value) return
  if (String(remindSelect.getValue()) !== value) {
    remindSelect.setValue(value, true)
  }
})

function toUaDigits10(anyPhone) {
  let digits = normalizePhoneDigits(String(anyPhone || ''))
  if (digits.startsWith('38')) digits = digits.slice(2)
  if (digits.length > 10) digits = digits.slice(-10)
  if (digits.length === 9) digits = '0' + digits
  return digits
}

async function applyCustomerPhoneToMask() {
  const raw = props.customer?.phone
  if (!raw) return
  if (phoneState.value?.hasAnyInput) return
  if (phoneLocal.value) return

  await nextTick()

  const digits10 = toUaDigits10(raw)
  if (!digits10) return

  if (mask?.value) {
    mask.value.unmaskedValue = digits10
    phoneLocal.value = mask.value.value
    return
  }

  setMaskedValue?.(digits10)
}

function validatePhone() {
  const s = phoneState.value

  if (!s?.hasAnyInput) {
    phoneError.value = t('phoneRequired')
    return false
  }
  if (!s.isComplete) {
    phoneError.value = t('phoneNotFull')
    return false
  }
  if (!s.formatOk) {
    phoneError.value = t('phoneNotValid')
    return false
  }
  if (!s.operatorOk) {
    phoneError.value = t('phoneOperatorNotValid')
    return false
  }

  phoneError.value = ''
  return true
}

const cityQuery = ref('')
const departmentQuery = ref('')
const citySuggestions = ref([])
const departmentSuggestions = ref([])
const cityOpen = ref(false)
const departmentOpen = ref(false)
const cityActiveIndex = ref(-1)
const departmentActiveIndex = ref(-1)
const cityError = ref('')
const departmentError = ref('')
const deliveryCityId = ref('')
const isCityEditing = ref(false)
const selectedCityHasNoDelivery = ref(false)
let cityAvailabilityRequestId = 0

const cityListboxId = 'np_city_suggestions'
const departmentListboxId = 'np_department_suggestions'
const cityListVisible = computed(() => cityOpen.value && citySuggestions.value.length > 0)
const departmentListVisible = computed(() => (
  departmentOpen.value && departmentSuggestions.value.length > 0
))
const activeCityOptionId = computed(() => (
  cityListVisible.value && cityActiveIndex.value >= 0
    ? getCityOptionId(cityActiveIndex.value)
    : ''
))
const activeDepartmentOptionId = computed(() => (
  departmentListVisible.value && departmentActiveIndex.value >= 0
    ? getDepartmentOptionId(departmentActiveIndex.value)
    : ''
))

const getCityOptionId = (index) => `${cityListboxId}-option-${index}`
const getDepartmentOptionId = (index) => `${departmentListboxId}-option-${index}`

function syncDeliveryUiFromProps() {
  const d = props.delivery || {}

  cityQuery.value = String(d.cityLabel || '')
  departmentQuery.value = String(
    d.fields?.np_department_label || d.fields?.np_department || ''
  )
  deliveryCityId.value = String(d.deliveryCityId || '')
  if (deliveryCityId.value || !String(d.cityLabel || '').trim()) {
    selectedCityHasNoDelivery.value = false
  }
}

function parseCityLabel(label) {
  const s = String(label || '').trim()
  const city = s.split('(')[0]?.trim() || ''
  const province = s.includes('(') ? s.split('(')[1].replace(')', '').trim() : ''
  return { city, province }
}

function emitDeliveryUpdate(extra = {}) {
  const cityLabel = extra.cityLabel ?? cityQuery.value
  const { city, province } = parseCityLabel(cityLabel)

  emit('update:delivery', {
    cityLabel,
    city: extra.city ?? city,
    province: extra.province ?? province,
    type: 1,
    deliveryTypeId: extra.deliveryTypeId ?? 'np_delivery_type_department',
    deliveryCityId: String(extra.deliveryCityId ?? deliveryCityId.value ?? ''),
    fields: {
      np_department: String(extra.np_department ?? '').trim(),
      np_department_label: String(
        extra.np_department_label ?? departmentQuery.value ?? ''
      ).trim(),
    },
  })
}

function debounce(fn, wait = 250) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), wait)
  }
}

async function fetchJson(url, params) {
  const qs = new URLSearchParams(params).toString()
  const res = await fetch(`${url}?${qs}`, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return await res.json()
}

async function resolveDeliveryCityIdFromProps() {
  const d = props.delivery || {}

  const existingCityId = String(d.deliveryCityId || '').trim()
  if (existingCityId) {
    deliveryCityId.value = existingCityId
    return existingCityId
  }

  const cityName = String(d.city || '').trim()
  const provinceName = String(d.province || '').trim()
  const cityLabel = String(d.cityLabel || '').trim()

  const query = cityName || cityLabel
  if (!query) return ''

  try {
    const cities = await fetchJson(CheckoutConf.url.getNpCity, {
      query,
      shop_name: CheckoutConf.shopName,
    })

    const list = Array.isArray(cities) ? cities : []

    const match =
      list.find((item) => {
        const label = String(item.label || '').toLowerCase()
        const area = String(item.meta?.Area || '').toLowerCase()
        const region = String(item.meta?.Region || '').toLowerCase()
        const province = provinceName.toLowerCase()
        const city = cityName.toLowerCase()

        return (!city || label.includes(city)) && (
          !province ||
          label.includes(province) ||
          area.includes(province) ||
          region.includes(province)
        )
      }) || list[0]

    const resolvedCityId = String(match?.delivery_city_id || '')
    if (!resolvedCityId) return ''

    deliveryCityId.value = resolvedCityId

    emitDeliveryUpdate({
      cityLabel: String(match?.label || cityLabel || query),
      city: String(match?.meta?.MainDescription || cityName),
      province: String(match?.meta?.Area || match?.meta?.Region || provinceName),
      deliveryCityId: resolvedCityId,
      np_department: String(d.fields?.np_department || ''),
      np_department_label: String(
        d.fields?.np_department_label || d.fields?.np_department || ''
      ),
    })

    return resolvedCityId
  } catch (e) {
    return ''
  }
}

async function hydrateDepartmentLabelFromProps() {
  const d = props.delivery || {}
  const departmentNum = String(d.fields?.np_department || '').trim()
  const queryValue = String(
    d.fields?.np_department_label || departmentQuery.value || departmentNum
  ).trim()

  if (!departmentNum) return

  const currentLabel = String(d.fields?.np_department_label || '').trim()

  if (currentLabel && currentLabel !== departmentNum) {
    departmentQuery.value = currentLabel
    return
  }

  let cityId = String(d.deliveryCityId || '').trim()

  if (!cityId) {
    cityId = await resolveDeliveryCityIdFromProps()
  }

  if (!cityId) {
    departmentQuery.value = departmentNum
    return
  }

  try {
    const departments = await fetchJson(CheckoutConf.url.getNpDepartment, {
      query: queryValue || departmentNum,
      shop_name: CheckoutConf.shopName,
      city_id: cityId,
    })

    const list = Array.isArray(departments) ? departments : []

    const depMatch =
      list.find((item) => String(item.meta?.number || '').trim() === departmentNum) ||
      list.find((item) => String(item.label || item.value || '').includes(`№${departmentNum}`)) ||
      list[0]

    if (!depMatch) {
      departmentQuery.value = departmentNum
      return
    }

    const label = String(depMatch.label || depMatch.value || departmentNum)

    departmentQuery.value = label
    deliveryCityId.value = cityId

    emitDeliveryUpdate({
      deliveryCityId: cityId,
      np_department: String(depMatch.meta?.number || departmentNum),
      np_department_label: label,
    })
  } catch (e) {
    departmentQuery.value = departmentNum
  }
}

async function hydrateDeliveryUiFromProps() {
  if (isCityEditing.value) return

  syncDeliveryUiFromProps()
  await nextTick()
  await hydrateDepartmentLabelFromProps()
}

watch(
  () => props.delivery,
  async () => {
    await hydrateDeliveryUiFromProps()
  },
  { immediate: true, deep: true }
)

const loadCities = debounce(async (term) => {
  if (!term || term.trim().length < 3) {
    citySuggestions.value = []
    cityActiveIndex.value = -1
    return
  }
  try {
    const data = await fetchJson(CheckoutConf.url.getNpCity, {
      query: term,
      shop_name: CheckoutConf.shopName,
    })
    citySuggestions.value = Array.isArray(data) ? data : []
    cityActiveIndex.value = citySuggestions.value.length ? 0 : -1
  } catch {
    citySuggestions.value = []
    cityActiveIndex.value = -1
  }
}, 250)

function onCityInput() {
  cityAvailabilityRequestId += 1
  isCityEditing.value = true
  selectedCityHasNoDelivery.value = false
  cityError.value = ''
  cityOpen.value = true
  cityActiveIndex.value = -1
  deliveryCityId.value = ''
  departmentQuery.value = ''
  departmentSuggestions.value = []
  closeDepartmentList()
  emitDeliveryUpdate({
    deliveryCityId: '',
    np_department: '',
    np_department_label: '',
  })
  loadCities(cityQuery.value)
}
function onCityFocus() {
  if (citySuggestions.value.length) {
    cityOpen.value = true
    if (cityActiveIndex.value < 0) cityActiveIndex.value = 0
  }
}
function onCityBlur() {
  setTimeout(() => {
    closeCityList()
    if (String(cityQuery.value || '').trim() && !deliveryCityId.value) validateCity()
  }, 100)
}

function closeCityList() {
  cityOpen.value = false
  cityActiveIndex.value = -1
}

function getCitySuggestionLabel(item) {
  return String(item?.label || '')
}

function moveCityActive(delta) {
  const count = citySuggestions.value.length
  if (!count) return

  cityOpen.value = true

  if (cityActiveIndex.value < 0) {
    cityActiveIndex.value = delta > 0 ? 0 : count - 1
    return
  }

  cityActiveIndex.value = (cityActiveIndex.value + delta + count) % count
}

function onCityKeydown(event) {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    moveCityActive(1)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    moveCityActive(-1)
    return
  }

  if (event.key === 'Enter' && cityListVisible.value && cityActiveIndex.value >= 0) {
    event.preventDefault()
    selectCity(citySuggestions.value[cityActiveIndex.value])
    return
  }

  if (event.key === 'Escape' && cityOpen.value) {
    event.preventDefault()
    closeCityList()
  }
}

async function checkCityDeliveryAvailability(cityId) {
  const requestId = ++cityAvailabilityRequestId

  if (!cityId) return false

  try {
    const data = await fetchJson(CheckoutConf.url.getNpDepartment, {
      query: '1',
      shop_name: CheckoutConf.shopName,
      city_id: cityId,
    })

    if (requestId !== cityAvailabilityRequestId) return true

    return Array.isArray(data) && data.length > 0
  } catch {
    return true
  }
}

async function selectCity(item) {
  if (!item) return
  const selectedDeliveryCityId = String(item.delivery_city_id || '')

  isCityEditing.value = false
  cityQuery.value = getCitySuggestionLabel(item)
  selectedCityHasNoDelivery.value = !selectedDeliveryCityId
  cityError.value = selectedCityHasNoDelivery.value ? t('cityErrorNoDelivery') : ''
  deliveryCityId.value = selectedDeliveryCityId

  departmentQuery.value = ''
  departmentSuggestions.value = []
  closeDepartmentList()

  emitDeliveryUpdate({
    cityLabel: item.label,
    city: String(item.meta?.MainDescription || ''),
    province: String(item.meta?.Area || item.meta?.Region || ''),
    deliveryCityId: selectedDeliveryCityId,
    np_department: '',
    np_department_label: '',
  })

  closeCityList()
  citySuggestions.value = []

  if (!selectedDeliveryCityId) return

  const hasDelivery = await checkCityDeliveryAvailability(selectedDeliveryCityId)
  if (hasDelivery) return

  selectedCityHasNoDelivery.value = true
  cityError.value = t('cityErrorNoDelivery')
  deliveryCityId.value = ''

  emitDeliveryUpdate({
    cityLabel: item.label,
    city: String(item.meta?.MainDescription || ''),
    province: String(item.meta?.Area || item.meta?.Region || ''),
    deliveryCityId: '',
    np_department: '',
    np_department_label: '',
  })
}

function clearCity() {
  cityAvailabilityRequestId += 1
  isCityEditing.value = false
  selectedCityHasNoDelivery.value = false
  cityQuery.value = ''
  departmentQuery.value = ''
  deliveryCityId.value = ''
  citySuggestions.value = []
  departmentSuggestions.value = []
  closeCityList()
  closeDepartmentList()

  emitDeliveryUpdate({
    cityLabel: '',
    city: '',
    province: '',
    deliveryCityId: '',
    np_department: '',
    np_department_label: '',
  })
}

const loadDepartments = debounce(async (term) => {
  if (!term || term.trim().length < 1) {
    departmentSuggestions.value = []
    departmentActiveIndex.value = -1
    return
  }

  const dCityId = deliveryCityId.value
  if (!dCityId) {
    departmentSuggestions.value = []
    departmentActiveIndex.value = -1
    return
  }

  try {
    const data = await fetchJson(CheckoutConf.url.getNpDepartment, {
      query: term,
      shop_name: CheckoutConf.shopName,
      city_id: dCityId,
    })
    departmentSuggestions.value = Array.isArray(data) ? data : []
    departmentActiveIndex.value = departmentSuggestions.value.length ? 0 : -1
  } catch {
    departmentSuggestions.value = []
    departmentActiveIndex.value = -1
  }
}, 200)

function onDepartmentInput() {
  departmentError.value = ''
  departmentOpen.value = true
  departmentActiveIndex.value = -1
  loadDepartments(departmentQuery.value)
}
function onDepartmentFocus() {
  if (departmentSuggestions.value.length) {
    departmentOpen.value = true
    if (departmentActiveIndex.value < 0) departmentActiveIndex.value = 0
  }
}
function onDepartmentBlur() {
  setTimeout(() => closeDepartmentList(), 100)
}

function closeDepartmentList() {
  departmentOpen.value = false
  departmentActiveIndex.value = -1
}

function getDepartmentSuggestionLabel(item) {
  return String(item?.label || item?.value || '')
}

function moveDepartmentActive(delta) {
  const count = departmentSuggestions.value.length
  if (!count) return

  departmentOpen.value = true

  if (departmentActiveIndex.value < 0) {
    departmentActiveIndex.value = delta > 0 ? 0 : count - 1
    return
  }

  departmentActiveIndex.value = (departmentActiveIndex.value + delta + count) % count
}

function onDepartmentKeydown(event) {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    moveDepartmentActive(1)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    moveDepartmentActive(-1)
    return
  }

  if (
    event.key === 'Enter' &&
    departmentListVisible.value &&
    departmentActiveIndex.value >= 0
  ) {
    event.preventDefault()
    selectDepartment(departmentSuggestions.value[departmentActiveIndex.value])
    return
  }

  if (event.key === 'Escape' && departmentOpen.value) {
    event.preventDefault()
    closeDepartmentList()
  }
}
function selectDepartment(item) {
  if (!item) return
  departmentQuery.value = getDepartmentSuggestionLabel(item)
  departmentError.value = ''

  emitDeliveryUpdate({
    np_department: String(item.meta?.number || ''),
    np_department_label: item.label || item.value || '',
  })

  closeDepartmentList()
  departmentSuggestions.value = []
}

function validateCity() {
  if (!String(cityQuery.value || '').trim()) {
    cityError.value = t('cityErrorEmpty')
    return false
  }

  if (selectedCityHasNoDelivery.value) {
    cityError.value = t('cityErrorNoDelivery')
    return false
  }

  if (!String(deliveryCityId.value || '').trim()) {
    cityError.value = t('cityErrorNoChoose')
    return false
  }

  cityError.value = ''
  return true
}

function setCityError(message = '') {
  cityError.value = message || t('cityErrorNoDelivery')
  selectedCityHasNoDelivery.value = true
}

function validateDepartment() {
  const val = String(departmentQuery.value || '').trim()
  if (!val) {
    departmentError.value = t('npErrorEmpty')
    return false
  }

  departmentError.value = ''
  return true
}

function validateAgreement() {
  if (!agreementLocal.value) {
    agreementError.value = t('checkmarkError')
    return false
  }
  agreementError.value = ''
  return true
}

watch(agreementLocal, () => {
  if (agreementError.value) validateAgreement()
})

watch(phoneLocal, () => {
  if (phoneError.value) validatePhone()
})

async function validateStep() {
  if (props.isLoading) return false

  const cityValid = validateCity()
  const departmentValid = validateDepartment()
  const phoneValid = validatePhone()
  const agreementValid = validateAgreement()

  await nextTick()

  if (!cityValid) {
    cityInputEl.value?.focus()
    return false
  }

  if (!departmentValid) {
    departmentInputEl.value?.focus()
    return false
  }

  if (!phoneValid) {
    phoneInputEl.value?.focus()
    return false
  }

  if (!agreementValid) {
    agreementInputEl.value?.focus()
    return false
  }

  return true
}

defineExpose({
  validateStep,
  setCityError,
})

watch(
  () => props.isOpen,
  async (open) => {
    if (open) {
      await nextTick()
      initRemindSelect()
      await hydrateDeliveryUiFromProps()
      await applyCustomerPhoneToMask()
    } else {
      destroyRemindSelect()
    }
  },
  { immediate: true }
)

watch(
  () => props.isLoading,
  async (loading) => {
    if (loading) return
    if (!props.isOpen) return

    await nextTick()
    initRemindSelect()
  }
)

onBeforeUnmount(() => {
  destroyRemindSelect()
})
</script>
